import { useMemo } from 'react';
import { IconBox } from '@/components/UI/IconBox/IconBox.styles';
import { getPaymentMethodsInfo } from '@/components/Payment/PaymentMethods.helpers';
import {
  PaymentOption,
  UsePaymentOptionsParams,
} from '@/components/Payment/PaymentMethods/types';
import { PaymentMethod } from '@/api/types/payments.types';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';

export function usePaymentOptions({
  t,
  paymentMethods,
  walletBalance,
  isAddBalanceOpen,
  isAuthenticated,
  isWebView,
  isB2B,
  isAppleGooglePayEnabled,
  isIos,
  isAndroid,
}: UsePaymentOptionsParams) {
  return useMemo<PaymentOption[]>(
    () =>
      getPaymentMethodsInfo(t, paymentMethods ?? [], walletBalance)
        .filter(({ value }) => {
          const shouldHideWallet =
            value === PaymentMethod.Wallet &&
            (isAddBalanceOpen || !isAuthenticated);
          const shouldHidePortmanat =
            value === PaymentMethod.Portmanat && isWebView;

          return !shouldHideWallet && !shouldHidePortmanat;
        })
        .map(({ label, value }) => {
          const isTerminal = value === PaymentMethod.Terminal;
          const isWallet = value === PaymentMethod.Wallet;
          const isBankCard = value === PaymentMethod.Card;
          const shouldShowAppleGooglePayIcon =
            isBankCard && isAppleGooglePayEnabled && (isIos || isAndroid);

          return {
            label,
            value,
            ...(isB2B && isWallet && { order: -1 }),
            ...(isTerminal && {
              labelIcon: (
                <IconBox
                  $filename="payment-terminal.svg"
                  $width={15}
                  $height={30}
                  $isMirroredX
                />
              ),
            }),
            ...(shouldShowAppleGooglePayIcon && {
              labelIcon: (
                <IconBox
                  data-testid={
                    isIos
                      ? AdPageTestIds.paymentProviderIcons.applePayIcon
                      : AdPageTestIds.paymentProviderIcons.googlePayIcon
                  }
                  $filename={isIos ? 'apple-pay.svg' : 'google-pay.svg'}
                  $width={48}
                  $height={30}
                />
              ),
            }),
          };
        }),
    [
      isAddBalanceOpen,
      isAndroid,
      isAppleGooglePayEnabled,
      isAuthenticated,
      isB2B,
      isIos,
      isWebView,
      paymentMethods,
      t,
      walletBalance,
    ]
  );
}
